<!-- View for selecting a report to view the app as -->
<template>
	<div class="h-full overflow-auto">
		<HeaderNav />
		<div
			class="flex flex-col items-center pt-16"
			v-auto-animate
		>
			<img
				src="/assets/logos/studio_name_mark_gradient.svg"
				alt="Sales Studio logo"
				class="mt-4 w-72"
			/>

			<div
				class="mt-28"
				v-if="loading"
			>
				<img
					:src="spinner"
					class="mt-4 size-5 w-full animate-spin"
				/>
			</div>
			<BaseAlert
				type="critical"
				class="mt-28"
				v-if="error"
			>
				{{ error }}
				<button
					type="button"
					class="ml-2 rounded-xs border border-white p-1 text-xs font-normal text-white uppercase hover:bg-white hover:text-red-700"
					@click="fetchReports"
				>
					{{ t('general.retry') }}
				</button>
			</BaseAlert>
			<div
				class="mt-28 mb-16 w-full max-w-177.5"
				v-auto-animate
			>
				<div
					v-if="!loading"
					class="w-full divide-y divide-slate-100 pb-8 text-center"
				>
					<SalesContactListItem
						v-for="report in reports"
						:key="report.id"
						:id="report.id"
						:firstname="report.firstName"
						:lastname="report.lastName"
						:email="report.email"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { V4SalesContact, V3SalesContact } from '@/apiTypes'
import spinner from '@/assets/spinner.svg'
import BaseAlert from '@/components/common/BaseAlert.vue'
import HeaderNav from '@/components/common/HeaderNav.vue'
import SalesContactListItem from '@/components/login/SalesContactListItem.vue'
import { axiosInstance } from '@/services/axiosInstance'
import { Feature, useFeatureService } from '@/services/featureService'
import { useAuthStore } from '@/stores/auth'
import type { Paginated, SalesContactListItemResult } from '@/types'
import { captureException } from '@sentry/vue'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const auth = useAuthStore()
const { t } = useI18n()

const reports = ref<SalesContactListItemResult[]>([])
const loading = ref(false)
const error = ref('')
const isUsingSfIntegration = ref(
	useFeatureService().isEnabled(Feature.SalesforceIntegration)
)

type ApiSalesContact = {
	data: Array<V3SalesContact>
}

/** Get the list of sales contacts the user can view as */
async function fetchReports() {
	loading.value = true
	error.value = ''

	const hubId = auth.user?.hub.id
	const selfId = auth.getOwnSalesContactId()

	isUsingSfIntegration.value
		? await salesforceReports(selfId, hubId)
		: await crmReports(selfId, hubId)
}

async function salesforceReports(
	selfId: string | undefined,
	hubId: string | undefined
) {
	try {
		const result = await axiosInstance.get<Paginated<V4SalesContact>>(
			'/v4/contacts/workers?' +
				'scope=isAfp|isInWebPortalDropdown|viewableBy::' +
				hubId +
				'&itemsPerPage=500'
		)
		reports.value = result.data.data
			.map((contact) => ({
				id: contact.id,
				email: contact.email,
				firstName: contact.firstName,
				lastName: contact.lastName,
			}))
			.sort((a, b) => {
				if (a.id === selfId) {
					return -1
				} else if (b.id === selfId) {
					return 1
				}
				return a.lastName.localeCompare(b.lastName)
			})
	} catch (e: any) {
		console.error(e)
		captureException(e)
		error.value = e.message

		if (auth.sessionExpired) {
			auth.showLogin = true
			return
		}
	}
	loading.value = false
}

async function crmReports(
	selfId: string | undefined,
	hubId: string | undefined
) {
	try {
		const result = await axiosInstance.get<ApiSalesContact>(
			'/v3/counselors?viewableBy=' + hubId
		)
		reports.value = result.data.data
			.map((contact) => ({
				id: contact.id,
				email: contact.email,
				firstName: contact.firstname,
				lastName: contact.lastname,
			}))
			.sort((a, b) => {
				if (a.id === selfId) {
					return -1
				} else if (b.id === selfId) {
					return 1
				}
				return a.lastName.localeCompare(b.lastName)
			})
	} catch (e: any) {
		console.error(e)
		captureException(e)
		error.value = e.message

		if (auth.sessionExpired) {
			auth.showLogin = true
			return
		}
	}
	loading.value = false
}

onMounted(fetchReports)
</script>
